import { useState } from 'react';
import { useAuth } from '../../context';
import { Input, Button } from '../common';
import { useToastContext } from '../common/Toast';
import { hashPasswordWithSalt } from '../../utils/crypto';
import './auth-common.css';

const LoginForm = ({ onSuccess }) => {
  const { login, loading } = useAuth();
  const { showToast } = useToastContext();
  
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  
  const [formErrors, setFormErrors] = useState({});
  const [loginAttempts, setLoginAttempts] = useState(0);

  const validateForm = () => {
    const errors = {};
    
    if (!formData.username.trim()) {
      errors.username = '이메일을 입력해주세요';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.username)) {
      errors.username = '올바른 이메일 형식을 입력해주세요';
    }
    
    if (!formData.password) {
      errors.password = '비밀번호를 입력해주세요';
    }
    
    return errors;
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const getErrorMessage = (error) => {
    if (!error) return '로그인 중 오류가 발생했습니다.';
    
    if (error.includes('AUTH_FAILED') || error.includes('Authentication failed') || error.includes('Invalid username or password')) {
      return '이메일 또는 비밀번호가 올바르지 않습니다.';
    }
    
    if (error.includes('USER_NOT_FOUND') || error.includes('User not found')) {
      return '존재하지 않는 사용자입니다. 이메일을 확인해주세요.';
    }
    
    if (error.includes('INTERNAL_ERROR') || error.includes('Internal server error')) {
      return '서버에 일시적인 문제가 발생했습니다. 잠시 후 다시 시도해주세요.';
    }
    
    if (error.includes('Network Error') || error.includes('ECONNABORTED')) {
      return '네트워크 연결을 확인하고 다시 시도해주세요.';
    }
    
    return error || '로그인 중 오류가 발생했습니다.';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    setFormErrors({});
    
    const errors = validateForm();
    if (Object.keys(errors).length > 0) {
      setFormErrors(errors);
      return;
    }
    
    try {
      // 비밀번호 해시화
      const hashedPassword = hashPasswordWithSalt(formData.password, formData.username);
      const loginData = {
        ...formData,
        password: hashedPassword
      };
      
      const result = await login(loginData);
      
      if (result.success) {
        setLoginAttempts(0);
        showToast({
          type: 'success',
          title: '로그인 성공',
          message: '환영합니다!'
        });
        if (onSuccess) {
          onSuccess(result);
        }
      } else {
        setLoginAttempts(prev => prev + 1);
        showToast({
          type: 'error',
          title: '로그인 실패',
          message: getErrorMessage(result.error)
        });
      }
    } catch (err) {
      console.error('Login error:', err);
      setLoginAttempts(prev => prev + 1);
      showToast({
        type: 'error',
        title: '로그인 오류',
        message: getErrorMessage(err.message)
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="auth-form">
      <Input
        label="이메일"
        type="email"
        name="username"
        value={formData.username}
        onChange={handleInputChange}
        error={formErrors.username}
        placeholder="이메일을 입력하세요"
        disabled={loading}
        required
      />
      
      <Input
        label="비밀번호"
        type="password"
        name="password"
        value={formData.password}
        onChange={handleInputChange}
        error={formErrors.password}
        placeholder="비밀번호를 입력하세요"
        disabled={loading}
        required
      />
      
      <Button
        type="submit"
        variant="primary"
        size="md"
        loading={loading}
        disabled={loading}
        className="auth-form__submit"
      >
        로그인
      </Button>
    </form>
  );
};

export default LoginForm;